import { useEffect, useState, useMemo } from "react";
import useAxios from "../../../../hooks/useAxios";
import { useCompanyFilterContext } from "../../components/context/CompanyFilterContext";
import Constants from "../../../../Constants";
import { useCompanyStatsUI } from "./useCompanyStatsUI";
import useHistogram from "../../../../hooks/useHistogram";
import { renderAnalyticsStatCards } from "../config/superDashboardConfig";
import { formatMillisecondsTohours } from "../../../../Utils";
import { useCompanyChatSessions } from "./useCompanyChatSessions";
import { useCaseDistribution } from "./useCaseDistribution";
import { useCaseContentDistribution } from "./useCaseContentDistribution";
import { useIcdDisabledCases } from "./useIcdDisabledCases";
import useHistogramModal from "../../components/hooks/useHistogramModal";

export const useGetAnalytics = () => {
    const { selectedRange } = useCompanyFilterContext();
    const { getData, isLoading } = useAxios();

    const companyStatsUI = useCompanyStatsUI();
    const { isChatSessionsLoading, totalChatSessions, companyChatSessions } = useCompanyChatSessions();
    const { isIcdDisabledLoading, totalIcdDisabled, icdDisabledByCompany } = useIcdDisabledCases();

    const {
        treatmentDateCountDistribution,
        treatmentDateTextLengthDistribution,
        lifecarePlanLengthDistribution,
        selectedDistribution,
        isContentDistributionOpen,
        onOpenContentDistribution,
        closeContentDistribution
    } = useCaseContentDistribution();

    const {
        distribution: settlementDistribution,
        selectedDistribution: selectedSettlementDistribution,
        isOpen: isSettlementHistogramOpen,
        onOpen: onOpenSettlementHistogram,
        close: closeSettlementHistogram
    } = useCaseDistribution({ endpointKey: "settlementAnalytics", valuesKey: "settlementValues" });

    const { promptId,
        fetchDatabyPromptId,
        isTokenInput,
        setIsTokenInput
    } = useHistogram();

    const { histogramState, openHistogram, updateHistogram, closeHistogram } = useHistogramModal();

    const onOpenHistogramModal = async (promptId) => {
        openHistogram({
            title: promptId,
            subtitle: isTokenInput ? "Input-Tokens" : "Output-Tokens",
            isLoading: true
        });
        const data = await fetchDatabyPromptId(promptId);
        updateHistogram({ data, isLoading: false });
    };

    const [analytics, setAnalytics] = useState({
        successful: {},
        failed: {},
        p99: {},
        running: {},
        unfulfilledRunning: {},
        previouslyFailed: {},
        limboCases: {},
        badRecords: {},
    });

    useEffect(() => {
        const fetchAnalytics = async () => {
            const { startDate, endDate } = selectedRange;
            const baseUrl = Constants.ApiUrl.super.dashboard;

            const [
                successful,
                failed,
                p99,
                running,
                unfulfilledRunningCases,
                previouslyFailed,
                limboCases,
                badRecords
            ] = await Promise.all([
                getData(`${baseUrl.successfulAnalytics}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                getData(`${baseUrl.failedAnalytics}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                getData(`${baseUrl.p99Time}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                getData(`${baseUrl.runningCases}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                getData(`${baseUrl.runningCasesUnfulfilled}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                getData(`${baseUrl.previouslyFailed}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                getData(`${baseUrl.limboCases}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`),
                getData(`${baseUrl.badRecords}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`)
            ]);

            setAnalytics({
                successful: successful?.success ? successful : {},
                failed: failed?.success ? failed : {},
                p99: p99?.success ? p99 : {},
                running: running?.success ? running : {},
                unfulfilledRunning: unfulfilledRunningCases?.success ? unfulfilledRunningCases : {},
                previouslyFailed: previouslyFailed?.success ? previouslyFailed : {},
                limboCases: limboCases?.success ? limboCases : {},
                badRecords: badRecords?.success ? badRecords : {}
            });
        };

        fetchAnalytics();
    }, [selectedRange]);

    const successfulCasesData = analytics?.successful;
    const previouslyFailed = analytics.previouslyFailed;
    const limboCases = analytics.limboCases;
    const runningCases = analytics.running;
    const unfulfilledRunningCases = analytics.unfulfilledRunning;
    const p99Cases = analytics.p99;
    const failed = analytics.failed
    const badRecords = analytics.badRecords

    const previouslyFailedTableData = useMemo(() =>
        previouslyFailed?.byDatabase?.flatMap(dbData =>
            dbData.recoveredCases.map(caseId => ({
                database: dbData.database,
                caseId
            }))
        ) || []
        , [previouslyFailed]);

    const failedCasesTableData = useMemo(() =>
        failed?.byDatabase?.flatMap(dbData =>
            dbData.failedCases.map(caseId => ({
                database: dbData.database,
                caseId
            }))
        ) || []
        , [failed]);

    const nonSuccessfulCasesTableData = useMemo(() =>
        failed?.byDatabase?.flatMap(dbData =>
            dbData.failedCases.map(caseId => ({
                database: dbData.database,
                caseId
            }))
        ) || []
        , [failed]);

    const totalNonSuccessfulCases = useMemo(() => failed?.totalCompanies, [failed]);

    const limboCasesTableData = useMemo(() =>
        limboCases?.byDatabase?.flatMap(dbData =>
            dbData.caseIds.map(caseId => ({
                database: dbData.database,
                caseId,
                count: dbData.count
            }))
        ) || []
        , [limboCases]);

    const limboCasesCountData = useMemo(() =>
        limboCases?.byDatabase?.map(dbData => ({
            database: dbData.database,
            count: dbData.count
        })) || []
        , [limboCases]);

    const runningCasesTableData = useMemo(() =>
        runningCases?.data?.cases?.map(item => ({
            dbName: item.dbName,
            caseId: item.caseId,
            runningJobId: item.runningJobId,
            runningFor: `${formatMillisecondsTohours(item.runningForMs)}`
        })) || []
        , [runningCases]);

    const failedRecordsTableData =
        badRecords?.byDatabase?.flatMap((dbData) =>
            dbData.failedRecords.map((recordId) => ({
                database: dbData.database,
                recordId,
            })),
        ) || [];

    const dashboardData = {
        previouslyFailedTableData,
        failedCasesTableData,
        runningCasesTableData,
        limboCasesTableData: limboCasesTableData,
        nonSuccessfulCasesTableData,
        failedRecordsTableData,
        successfulCasesData: successfulCasesData?.byDatabase,
        unfulfilledRunningCases: unfulfilledRunningCases?.data?.cases,
        p99Cases: p99Cases?.data?.caseIds,
        promptId,
        limboCasesCountData,
        companyChatSessions,
        icdDisabledByCompany,
        treatmentDateCountDistribution,
        treatmentDateTextLengthDistribution,
        lifecarePlanLengthDistribution,
        caseSettlementDistribution: settlementDistribution
    };

    const statCards = useMemo(
        () => renderAnalyticsStatCards({
            previouslyFailed,
            successfulCasesData,
            totalNonSuccessfulCases,
            runningCases,
            unfulfilledRunningCases,
            limboCases,
            p99Cases,
            isChatSessionsLoading,
            totalChatSessions,
            totalIcdDisabled,
            isIcdDisabledLoading,
            isLoading
        }),
        [previouslyFailed, successfulCasesData, totalNonSuccessfulCases, runningCases, unfulfilledRunningCases, limboCases, p99Cases, totalIcdDisabled, isIcdDisabledLoading, isLoading]
    );

    return {
        ...companyStatsUI,
        companyAnalyticsIsLoading: isLoading,
        statCards,
        dashboardData,
        isTokenInput,
        setIsTokenInput,
        onOpenHistogramModal,
        histogramState,
        closeHistogram,
        selectedDistribution,
        isContentDistributionOpen,
        onOpenContentDistribution,
        closeContentDistribution,
        selectedSettlementDistribution,
        isSettlementHistogramOpen,
        onOpenSettlementHistogram,
        closeSettlementHistogram
    };
};